/**
 * What an Operations routing submission would actually change.
 *
 * Kept apart from the screen so the rule that protects a booked branch can be
 * tested on its own: choosing the case's current lab again must start on its
 * current branch — never quietly on the main site — and an assignment that
 * changes nothing must not be submittable, so opening the form can never move
 * a worker's appointment by accident.
 */

export interface CurrentAssignment {
  partnerId: string | null;
  branchId: string | null;
  physicianId: string | null;
  /**
   * Whether the case has a booked (scheduled or confirmed) appointment.
   * Booking again at the same lab does not cancel it, so a second slot there
   * would double-book the worker; a new slot is only offered when the lab
   * moves (which cancels the old one) or when nothing is booked.
   */
  hasActiveAppointment?: boolean;
}

export interface ProposedAssignment {
  partnerId: string;
  branchId: string;
  physicianId: string;
  slot: string;
}

/** The branch a lab starts on when it is chosen: its current one if it is the case's lab. */
export function initialBranch(chosenPartnerId: string, current: CurrentAssignment): string {
  return chosenPartnerId === current.partnerId ? (current.branchId ?? '') : '';
}

export function assignmentChange(current: CurrentAssignment, next: ProposedAssignment) {
  const moving = !!current.partnerId;
  // Main site is the empty branch on both sides.
  const labChanged = !moving || next.partnerId !== current.partnerId || next.branchId !== (current.branchId ?? '');
  const physicianChanged = !!next.physicianId && next.physicianId !== current.physicianId;
  const canBook = !moving || labChanged || !current.hasActiveAppointment;
  const slot = canBook ? next.slot : '';
  const nothingChanges = moving && !!next.partnerId && !labChanged && !physicianChanged && !slot;
  /** Only a slot, at the lab the case already has: booking, not reassigning. */
  const bookingOnly = moving && !labChanged && !physicianChanged && !!slot;
  return {
    moving,
    labChanged,
    physicianChanged,
    nothingChanges,
    canBook,
    bookingOnly,
    /** Cancelling open appointments happens only when the lab or branch moves. */
    cancelsAppointments: moving && labChanged && !!next.partnerId,
    actionLabel: moving
      ? (labChanged ? 'Change lab' : bookingOnly ? 'Book appointment' : 'Update assignment')
      : slot ? 'Assign lab and book' : 'Assign lab',
  };
}
